import { store } from "./store";
import { clearCourse, resetUserState } from "./userSlice";

export interface DownloadFileResult {
	ok: boolean;
	error?: string;
}

/**
 * Downloads a file from the given URL using fetch() rather than a plain
 * browser navigation (e.g. <a href download>). This is important because the
 * server's cookie-authentication middleware only knows a request is "ajax"
 * (and should therefore respond with a 401/403 status code) when it sees an
 * X-Requested-With header. A plain navigation/link click never sends that
 * header, so an expired or missing session would cause the server to 302
 * redirect to the HTML login page, and the browser would save *that HTML* as
 * the downloaded file (e.g. "usage-summary.csv" containing login markup).
 *
 * By using fetch() here we can:
 *   1) Send the same X-Requested-With / X-Application headers the rest of
 *      the SPA sends, so the server treats this as an ajax call and returns
 *      a proper 401/403 status instead of redirecting to HTML.
 *   2) Inspect the response status before ever writing anything to disk, so
 *      we never save an error page as if it were the requested file.
 */
export async function downloadFile(
	url: string,
	filenameFallback?: string
): Promise<DownloadFileResult> {
	let response: Response;
	try {
		response = await fetch(url, {
			method: "GET",
			credentials: "include",
			mode: "cors",
			headers: {
				"X-Requested-With": "XMLHttpRequest",
				"X-Application": "MIWriteSpa",
			},
		});
	} catch {
		return {
			ok: false,
			error: "Something went wrong downloading the file. Please check your connection and try again.",
		};
	}

	if (response.status === 401) {
		store.dispatch(resetUserState());
		return { ok: false, error: "Your session has expired. Please log in again." };
	}

	if (response.status === 403) {
		store.dispatch(clearCourse());
		return { ok: false, error: "You do not have permission to download this file." };
	}

	if (!response.ok) {
		return {
			ok: false,
			error: "Something went wrong downloading the file. Please try again.",
		};
	}

	const blob = await response.blob();
	const filename = getFilenameFromContentDisposition(response.headers.get("Content-Disposition"))
		?? filenameFallback
		?? "download";

	const objectUrl = URL.createObjectURL(blob);
	try {
		const link = document.createElement("a");
		link.href = objectUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	} finally {
		// Revoke asynchronously to make sure the download has been handed off
		// to the browser before the object URL is invalidated.
		setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
	}

	return { ok: true };
}

function getFilenameFromContentDisposition(header: string | null): string | undefined {
	if (!header) return undefined;

	// Prefer the RFC 5987 filename*=UTF-8''... form when present.
	const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
	if (utf8Match) {
		try {
			return decodeURIComponent(utf8Match[1]);
		} catch {
			// fall through to the simple form below
		}
	}

	const simpleMatch = /filename="?([^";]+)"?/i.exec(header);
	return simpleMatch ? simpleMatch[1] : undefined;
}
